import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BasicTable, Button, Column, ContentBox, ContentPage, NoResults, Row, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useHasCltContract from "@hooks/useHasCltContract";
import AlertViewModal from "@components/AlertViewModal";
import { pendingQuizzes, pendingQuizzesText } from "@helpers/dashboard";
import { escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { AwaitingInvoiceNotices, useAwaitingInvoices } from "../../Common/TaxInvoices";
import {
  AllClear,
  DASHBOARD_HEADER,
  DashboardBlock,
  FitBox,
  PendingNotice,
  PendingSkeleton,
  Stack,
  StatCards,
  totalOf,
} from "../../Common/Dashboard";

// Dashboard do prestador (/ do EMP; modelo Dashboard do DS OneWork): o ponto (com
// contrato CLT), os números dele, o que espera por ele (as notas a enviar, como em
// Notas fiscais, e os questionários) e os últimos comunicados da empresa. Sem
// scope=company, o back devolve só o que é da pessoa.

const Between = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const Tight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Texto em até 2 linhas; o inteiro fica no title e no "Ver comunicado"
const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

// O back devolve só os ativos, com o totalAnswers da pessoa; 100 é o máximo do limit
const QUIZ_QUERY = "page=1&limit=100";

export default function Dashboard() {
  const navigate = useNavigate();
  const documentsService = useService("documents");
  const taxInvoicesService = useService("taxInvoices");
  const declarationService = useService("declarations");
  const quizService = useService("quiz");
  const alertsService = useService("alerts");

  const hasCltContract = useHasCltContract();
  const awaitingInvoices = useAwaitingInvoices();
  const [stats, setStats] = useState(null);
  const [quizzes, setQuizzes] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [viewing, setViewing] = useState(null);

  usePageHeader(DASHBOARD_HEADER);

  useEffect(() => {
    fetchStats();
    fetchQuizzes();
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchStats() {
    const [documents, invoices, declarations] = await Promise.allSettled([
      documentsService.count(),
      taxInvoicesService.count(),
      declarationService.list("myList=1&page=1&limit=1"),
    ]);
    setStats({ documents: totalOf(documents), invoices: totalOf(invoices), declarations: totalOf(declarations) });
  }

  async function fetchQuizzes() {
    try {
      const result = await quizService.list(QUIZ_QUERY);
      setQuizzes(pendingQuizzes(result?.data?.list, result?.data?.totalRegisters));
    } catch (error) {
      // Sem a lista, o aviso de questionários não aparece
      console.error(error);
      setQuizzes({ count: 0, exact: true });
    }
  }

  async function fetchAlerts() {
    const result = await alertsService.getListEmpDash();
    setAlerts(result && !result.error && Array.isArray(result.data) ? result.data : []);
  }

  const statItems = [
    { key: "documents", label: "Documentos", value: stats?.documents, link: "Ver documentos", route: "/e/documents" },
    {
      key: "invoices",
      label: "Notas fiscais enviadas",
      value: stats?.invoices,
      link: "Ver notas fiscais",
      route: "/e/nfes",
    },
    {
      key: "declarations",
      label: "Declarações enviadas",
      value: stats?.declarations,
      link: "Ver declarações",
      route: "/e/declarations",
    },
  ];

  const quizText = quizzes ? pendingQuizzesText(quizzes) : "";
  const pendingLoading = awaitingInvoices.loading || !quizzes;
  const nothingPending = awaitingInvoices.awaiting.length === 0 && !quizText;

  const alertColumns = [
    {
      id: "text",
      header: "Comunicado",
      cell: ({ row }) => (
        <div>
          <Clamp title={row.original.text || ""}>
            <Text $text={escapeHtml(row.original.text || "")} $variant="paragraph_small_medium" $color="N900" />
          </Clamp>
          {row.original.image_url ? <Text $text="Com imagem" $variant="caption_medium" $color="N400" /> : null}
        </div>
      ),
    },
    { id: "created_at", header: "Criado em", size: 150, cell: ({ row }) => formatDateTime(row.original.created_at) },
  ];

  return (
    <ContentPage>
      {hasCltContract ? (
        <FitBox>
          <ContentBox>
            <Between>
              <Tight>
                <Text $text="Ponto" $variant="h4_bold" $color="N900" />
                <Text $text="Registre sua batida e acompanhe seu espelho de ponto." $variant="caption_medium" $color="N600" />
              </Tight>
              <Button $text="Registrar ponto" $variant="primary" $noWidth $action={() => navigate("/e/time-clock")} />
            </Between>
          </ContentBox>
        </FitBox>
      ) : null}

      <FitBox>
        <ContentBox>
          <StatCards items={statItems} loading={!stats} />
        </ContentBox>
      </FitBox>

      <ContentBox>
        <Row $gap={30} $alignItems="flex-start">
          <Column $columns={7}>
            <DashboardBlock title="Pendências">
              {pendingLoading ? (
                <PendingSkeleton />
              ) : nothingPending ? (
                <AllClear text="Nenhuma nota ou questionário esperando você." />
              ) : (
                <Stack>
                  <AwaitingInvoiceNotices
                    awaiting={awaitingInvoices.awaiting}
                    onSent={() => {
                      awaitingInvoices.reload();
                      fetchStats();
                    }}
                  />
                  {quizText ? (
                    <PendingNotice title="Questionários" text={quizText} action="Responder" route="/e/quiz" />
                  ) : null}
                </Stack>
              )}
            </DashboardBlock>
          </Column>
          <Column $columns={5}>
            <DashboardBlock title="Comunicados">
              {!alerts ? (
                <PendingSkeleton />
              ) : alerts.length === 0 ? (
                <NoResults notResultText="Nenhum comunicado por aqui" />
              ) : (
                <BasicTable
                  data={alerts}
                  columns={alertColumns}
                  getRowId={(row) => String(row.id)}
                  onRowClick={(row) => setViewing(row)}
                />
              )}
            </DashboardBlock>
          </Column>
        </Row>
      </ContentBox>

      {viewing ? <AlertViewModal alert={viewing} onClose={() => setViewing(null)} /> : null}
    </ContentPage>
  );
}
